<script>
  import { get, call } from 'vuex-pathify'
  import screen from '@/helpers/screen'
  import AccountProfileGrid from '@/components/account/AccountProfileGrid'
  import HomeMemberProfile from '@/components/home/HomeMemberProfile'
  import createGuardMixin from '@/helpers/createGuardMixin'
  import { ApiError } from '@/api'

  // Remove feature flag check once member dashboard is live
  const guardMixin = createGuardMixin(
    async ({ store, redirect }) => {
      const isLoggedIn = await store.dispatch('account/loggedInAsync')
      if (!isLoggedIn) {
        return redirect({ name: 'AccountLogin' })
      }
    },
    ['isLoggedIn']
  )

  export default {
    components: {
      AccountProfileGrid,
      HomeMemberProfile,
    },
    mixins: [guardMixin],
    data() {
      return {
        tabs: [
          {
            to: '/account/settings',
            label: 'Settings',
          },
          {
            to: '/account/subscriptions',
            label: 'Memberships',
          },
          {
            to: '/account/orders',
            label: 'Orders',
          },
        ],
        sidebar_block: [],
      }
    },
    computed: {
      ...get(['route']),
      ...get('feature', ['flags']),
      ...get('general', ['isUnsubscribeAction', 'isReactivateAction']),
      ...get('prismic', ['accountSettingsData']),
      boxOnlyImage() {
        return this.accountSettingsData || ''
      },
      screen,
      isRouteNested() {
        return [
          'AccountProfileChangeSubscriptionAddress',
          'AccountProfileSettingsAddAddress',
          'AccountProfileSettingsChangePassword',
          'AccountProfileSettingsCreditCard',
          'AccountProfileSettingsEditAddress',
          'AccountProfileSettingsEditInfo',
          'AccountProfileSettingsPaymentMethod',
          'AccountProfileSettingsRemoveAddress',
          'AccountSubscriptionEdit',
        ].includes(this.route.name)
      },
      isFullScreenOnMobile() {
        return ['AccountProfileOrdersAll', 'AccountAddOn'].includes(this.route.name)
      },
      routeComponent() {
        return this.$router.getMatchedComponents(this.route).pop()
      },
      bgColor() {
        return !this.screen.md ? 'bg-dawn' : ''
      },
      filteredSidebarBlocks() {
        return this.accountSettingsData?.sidebar_block?.filter((block) => block.show_block) || []
      },
    },
    async created() {
      await this.loadAccountSettingsData()
      try {
        await this.$store.dispatch('account/ensureFreshProfile')
      } catch (error) {
        if (error instanceof ApiError) {
          return
        }
        return Promise.reject(error)
      }
    },
    methods: {
      ...call('prismic', ['loadAccountSettingsData']),
    },
  }
</script>

<template>
  <section>
    <div :class="`${isFullScreenOnMobile ? '' : 'min-h-screen overflow-hidden'} ${bgColor}`">
      <BaseTransitionView appear>
        <div v-if="isRouteNested && !screen.md" key="nested" class="pt-6 pb-8">
          <BaseContainer>
            <BaseTransitionView :class="bgColor">
              <component
                :is="routeComponent"
                :clicked-action="
                  route.name === 'AccountSubscriptionEdit' ? $route.params.clickedAction : undefined
                "
                :selected-subscription-id="
                  route.name === 'AccountSubscriptionEdit'
                    ? Number($route.params.selectedSubscriptionId)
                    : undefined
                "
                :subscription-type="
                  route.name === 'AccountSubscriptionEdit'
                    ? $route.params.subscriptionType
                    : undefined
                "
              />
            </BaseTransitionView>
          </BaseContainer>
        </div>
        <div v-else-if="isFullScreenOnMobile && !screen.md">
          <component
            :is="routeComponent"
            :subscription-id="
              route.name === 'AccountAddOn' ? Number($route.params.subscriptionId) : undefined
            "
            :type="route.name === 'AccountAddOn' ? $route.params.type : undefined"
          />
        </div>
        <div v-else key="root">
          <!-- TODO: replace placeholder banner and boxes -->
          <component :is="screen.md ? 'BaseContainer' : 'div'" max-width="2xl">
            <AccountProfileGrid class="md:pt-6">
              <div class="md:pb-8">
                <div class="mb-4 bg-white shadow-gray-border">
                  <HomeMemberProfile account-view />
                  <BaseDivider v-if="screen.md" />
                  <BaseTabs :tabs="tabs" />
                </div>
                <BaseTransitionView>
                  <RouterView />
                </BaseTransitionView>
              </div>
              <div class="flex flex-col">
                <BaseLink
                  v-for="block in filteredSidebarBlocks"
                  :key="block.button_link.key"
                  :href="block.button_link.url"
                  class="mb-4"
                >
                  <BaseBanner
                    :size="block.image.url ? 'medium-full' : 'medium-half'"
                    :class="`bg-${block.background_color.toLowerCase()}`"
                  >
                    <div v-if="screen.lg && block.image.url" class="flex justify-center">
                      <BaseImage
                        :src="block.image.url"
                        :alt="block.image.alt"
                        background-transparent
                        responsive="xs"
                        class="w-34 pb-2"
                      />
                    </div>
                    {{ block.title }}
                    <template v-slot:description>
                      {{ block.subtitle }}
                    </template>
                    <template v-if="block.button_text" v-slot:cta>{{ block.button_text }}</template>
                  </BaseBanner>
                </BaseLink>
              </div>
            </AccountProfileGrid>
          </component>
        </div>
      </BaseTransitionView>
    </div>
  </section>
</template>
